import React, {useEffect, useMemo, useState} from "react";
import TrendableMetric from "../../jsx/TrendableMetric";
import YearBreakdownMetric, { sumYearValues } from "../../jsx/YearBreakdownMetric";

import {computeYtdValue, useTrendModalHelper} from '../../utils/useTrendModalHelper';

import TrendGraphModal from "./TrendGraphModal";
import {fetchHistoricPayrolls} from "../../services/fetchHistoricPayrolls";
import useModalStore from "../../stores/modalStore";
import {BACKEND_URL} from "../../utils/constants";
import useUserStore from "../../stores/userStore";
import { hasPermission } from "../../utils/permissions";
import {dollarFormat, roundFormat, toFloat} from "../../utils/formatting";
import GenericModal from "./GenericModal";
import getFirebaseAuthToken from '../../services/fireBaseAuthenticationService';
import useSettingsStore from '../../stores/settingsStore';

const PAYROLLS_PER_YEAR = 24;

const getPayrollEndDate = (run) => new Date(`${run.period_end}T00:00:00`);

const getPayrollEndYear = (run) => getPayrollEndDate(run).getFullYear();

const getYearValue = (value, year) => {
    if (value && typeof value === "object" && value[year] !== undefined) {
        return sumYearValues(value[year]);
    }

    return sumYearValues(value);
};

const PaySummaryModal = ({
                             historicPayrolls,
                             setHistoricPayrolls
                         }) => {
    const show = useModalStore(state => state.showPaySummaryModal);
    const { userIdToFirstName, employees } = useUserStore();
    const piiClass = useSettingsStore(s => s.blurPII) ? 'blur-pii' : '';
    const [ selectedPeriodEnd, setSelectedPeriodEnd ] = useState(null);
    const modalProps = useModalStore(state => state.modalProps.paySummaryModal);
    const [userId, setUserId] = useState(modalProps?.userId);//Local state userId allows us to dynamically page through the employees (and change userId to be different from what was passed in the modal props)

    useEffect(() => {
        if (show && modalProps?.userId) {
            setUserId(modalProps.userId);
        }
    }, [show, modalProps?.userId]);


    const {
        trendData,
        trendTitle,
        xKey,
        yKey,
        openTrendModal
    } = useTrendModalHelper();

    const memoizedTrendGraphModal = useMemo(() => (
        <TrendGraphModal
            title={trendTitle}
            data={trendData}
            xKey={xKey}
            yKey={yKey}
        />
    ), [trendData, trendTitle, xKey, yKey]);


    //Load historic payrolls if they aren't loaded.
    useEffect(() => {
        if (show && historicPayrolls.length === 0) {
            const load = async () => {
                try {
                    const runs = await fetchHistoricPayrolls(getFirebaseAuthToken, BACKEND_URL);
                    setHistoricPayrolls(runs || []);
                } catch (err) {
                    console.error("Failed to fetch payroll runs:", err);
                    setHistoricPayrolls([]);
                }
            };

            load();
        }
    }, [show, historicPayrolls.length, setHistoricPayrolls]);

    //Go to the latest period on open
    useEffect(() => {
        if (show && historicPayrolls.length > 0 && !selectedPeriodEnd) {
            setSelectedPeriodEnd(historicPayrolls[historicPayrolls.length - 1].period_end);
        }
    }, [show, historicPayrolls, selectedPeriodEnd]);

    // Reset selectedPeriodEnd when modal closes
    useEffect(() => {
        if (!show) {
            setSelectedPeriodEnd(null);
        }
    }, [show]);

    if (!show || !userId) return null;

    // Find the current payroll run by period_end
    const currentPayrollRun = historicPayrolls.find(run => run.period_end === selectedPeriodEnd);
    
    // Get the employee's data for this specific period (may be null if not available)
    const currentEmployeePayDetail = currentPayrollRun?.employee_data?.find(e => e.user_id === userId)
        ? { ...currentPayrollRun.employee_data.find(e => e.user_id === userId), runMeta: currentPayrollRun }
        : null;

    const summaryPayrollRun = currentPayrollRun;
    const summaryPayrollYear = summaryPayrollRun ? getPayrollEndYear(summaryPayrollRun) : null;
    const completedPayrollsThisYear = summaryPayrollRun
        ? historicPayrolls.filter(run =>
            getPayrollEndYear(run) === summaryPayrollYear &&
            getPayrollEndDate(run) <= getPayrollEndDate(summaryPayrollRun)
        ).length
        : 0;
    const summaryPayrollEmployee = summaryPayrollRun?.employee_data?.find(e => e.user_id === userId);
    const summaryPayrollYtdIncome = summaryPayrollRun
        ? computeYtdValue(historicPayrolls, userId, "income_in_period.total_income", summaryPayrollRun.period_end)
        : 0;
    const summaryPayrollPeriodIncome = summaryPayrollEmployee
        ? toFloat(summaryPayrollEmployee.income_in_period?.total_income || 0)
        : 0;
    const summaryPayrollYtdHours = summaryPayrollRun
        ? computeYtdValue(historicPayrolls, userId, "normal_hours", summaryPayrollRun.period_end) +
            computeYtdValue(historicPayrolls, userId, "overtime_hours", summaryPayrollRun.period_end)
        : 0;
    const summaryPayrollYtdSickHours = summaryPayrollRun
        ? getYearValue(
            summaryPayrollEmployee?.year_sick_hours_through_period_end ??
            summaryPayrollEmployee?.YTD_sick_hours_prior_to_period ??
            summaryPayrollEmployee?.year_sick_hours_prior_to_period,
            summaryPayrollYear
        )
        : 0;
    const projectedYearEndIncome = completedPayrollsThisYear > 0
        ? (summaryPayrollYtdIncome / completedPayrollsThisYear) * PAYROLLS_PER_YEAR
        : 0;

    // For period navigation, use the full list of payroll runs
    const payrollRunIndex = historicPayrolls.findIndex(run => run.period_end === selectedPeriodEnd);
    
    const goToPeriod = (direction) => {
        const newIndex = payrollRunIndex + direction;
        if (newIndex >= 0 && newIndex < historicPayrolls.length) {
            setSelectedPeriodEnd(historicPayrolls[newIndex].period_end);
        }
    };

    return (
        <>
            <GenericModal
                name="paySummaryModal"
                title={<>Pay Summary for <span className={piiClass}>{userIdToFirstName[userId] || userId}</span></>}
                modalProps={{ userId }}
                hideButton={true}
                stickyFooter={
                    <div className="flex flex-col gap-3">
                        <div className="flex justify-center items-center gap-3">
                            <button
                                onClick={() => goToPeriod(-1)}
                                disabled={payrollRunIndex <= 0}
                                className="!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-50 disabled:!opacity-30 !transition-colors"
                            >
                                ← Prev Period
                            </button>
                            <span className="text-xs text-gray-400 font-medium">{payrollRunIndex + 1} / {historicPayrolls.length}</span>
                            <button
                                onClick={() => goToPeriod(1)}
                                disabled={payrollRunIndex >= historicPayrolls.length - 1}
                                className="!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-50 disabled:!opacity-30 !transition-colors"
                            >
                                Next Period →
                            </button>
                        </div>

                        {hasPermission('run_payroll') && (() => {
                            const currentIndex = employees.findIndex(e => e.user_id === userId);
                            const totalEmployees = employees.length;

                            const goToEmployee = (indexOffset) => {
                                const newIndex = (currentIndex + indexOffset + totalEmployees) % totalEmployees;
                                const newUserId = employees[newIndex].user_id;
                                setUserId(newUserId);
                            };

                            return (
                                <div className="flex justify-center items-center gap-3">
                                    <button
                                        onClick={() => goToEmployee(-1)}
                                        className="!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-50 !transition-colors"
                                    >
                                        ← Prev Employee
                                    </button>
                                    <span className="text-xs text-gray-400 font-medium">{currentIndex + 1} / {totalEmployees}</span>
                                    <button
                                        onClick={() => goToEmployee(1)}
                                        className="!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-50 !transition-colors"
                                    >
                                        Next Employee →
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                }
            >
                {currentPayrollRun ? (
                    <div className="overflow-y-auto p-4 flex flex-col max-h-[75vh] min-h-[60vh]">
                        {!currentEmployeePayDetail ? (
                            <div className="text-center py-8 text-gray-500">
                                <p><strong>Period:</strong> {currentPayrollRun.period_start} to {currentPayrollRun.period_end}</p>
                                <p className="mt-4">Not Available for this employee</p>
                            </div>
                        ) : (
                            <>

                        <p><strong>Period:</strong> {currentEmployeePayDetail.period_start} to {currentEmployeePayDetail.period_end}</p>
                        <p><strong>Employee:</strong> <span className={piiClass}>{currentEmployeePayDetail.Employee}</span></p>

                        {summaryPayrollRun && (
                            <div className="mt-4 mb-4 shrink-0 overflow-hidden rounded-xl border border-indigo-100 bg-indigo-50/40">
                                <div className="px-4 py-3 border-b border-indigo-100">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-700">YTD as of Displayed Payroll ({summaryPayrollRun.period_end})</h3>
                                    <p className="text-xs text-indigo-500">{completedPayrollsThisYear} of {PAYROLLS_PER_YEAR} payrolls complete</p>
                                </div>
                                <table className="w-full text-sm">
                                    <tbody>
                                    <tr className="border-b border-indigo-100">
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">Total Income</th>
                                        <td className="px-4 py-2 text-right font-semibold text-gray-900">{dollarFormat(summaryPayrollPeriodIncome)}</td>
                                    </tr>
                                    <tr className="border-b border-indigo-100">
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">YTD Total Income</th>
                                        <td className="px-4 py-2 text-right font-semibold text-gray-900">{dollarFormat(summaryPayrollYtdIncome)}</td>
                                    </tr>
                                    <tr className="border-b border-indigo-100">
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">YTD Hours</th>
                                        <td className="px-4 py-2 text-right font-semibold text-gray-900">{roundFormat(summaryPayrollYtdHours)}</td>
                                    </tr>
                                    <tr className="border-b border-indigo-100">
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">YTD Sick Hours</th>
                                        <td className="px-4 py-2 text-right font-semibold text-gray-900">{roundFormat(summaryPayrollYtdSickHours)}</td>
                                    </tr>
                                    <tr>
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">Projected Year-End Total Income</th>
                                        <td className="px-4 py-2 text-right font-semibold text-gray-900">{dollarFormat(projectedYearEndIncome)}</td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <TrendableMetric
                            label="Days Worked"
                            currentValue={currentEmployeePayDetail.days_worked}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="Days"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.days_worked || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Scheduled Hours"
                            currentValue={currentEmployeePayDetail.scheduled_hours}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="Scheduled Hours"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.scheduled_hours || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="YTD Scheduled Hours"
                            currentValue={computeYtdValue(
                                historicPayrolls,
                                userId,
                                "scheduled_hours",
                                currentEmployeePayDetail.runMeta.period_end
                            )}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="YTD Scheduled Hours"
                            openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentEmployeePayDetail.runMeta.period_end).getFullYear() })}
                            accessorFn={(p) =>
                                computeYtdValue(
                                    historicPayrolls,
                                    userId,
                                    "scheduled_hours",
                                    p.period_end
                                )
                            }
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Normal Hours"
                            currentValue={currentEmployeePayDetail.normal_hours}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="Hours"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.normal_hours || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="YTD Normal Hours"
                            currentValue={computeYtdValue(
                                historicPayrolls,
                                userId,
                                "normal_hours",
                                currentEmployeePayDetail.runMeta.period_end
                            )}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="YTD Normal Hours"
                            openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentEmployeePayDetail.runMeta.period_end).getFullYear() })}
                            accessorFn={(p) =>
                                computeYtdValue(
                                    historicPayrolls,
                                    userId,
                                    "normal_hours",
                                    p.period_end
                                )
                            }
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Overtime Hours"
                            currentValue={currentEmployeePayDetail.overtime_hours}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="Overtime Hours"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.overtime_hours || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="YTD Overtime Hours"
                            currentValue={computeYtdValue(
                                historicPayrolls,
                                userId,
                                "overtime_hours",
                                currentEmployeePayDetail.runMeta.period_end
                            )}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="YTD Overtime Hours"
                            openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentEmployeePayDetail.runMeta.period_end).getFullYear() })}
                            accessorFn={(p) =>
                                computeYtdValue(
                                    historicPayrolls,
                                    userId,
                                    "overtime_hours",
                                    p.period_end
                                )
                            }
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Percent of Scheduled Hours Worked"
                            currentValue={(() => {
                                const scheduled = toFloat(currentEmployeePayDetail.scheduled_hours || 0);
                                const worked = toFloat(currentEmployeePayDetail.normal_hours || 0) + toFloat(currentEmployeePayDetail.overtime_hours || 0);
                                return scheduled > 0 ? (worked / scheduled) * 100 : 0;
                            })()}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="Percent of Scheduled Hours Worked"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => {
                                const emp = p.employee_data.find(e => e.user_id === userId);
                                if (!emp) return 0;

                                const scheduled = toFloat(emp.scheduled_hours || 0);
                                const worked = toFloat(emp.normal_hours || 0) + toFloat(emp.overtime_hours || 0);
                                return scheduled > 0 ? (worked / scheduled) * 100 : 0;
                            }}
                            data={historicPayrolls}
                        />

                        <h3 className="mt-4 mb-1 text-xs font-bold uppercase tracking-wider text-gray-400 text-center">Services</h3>

                        <TrendableMetric
                            label="Services in Period"
                            currentValue={currentEmployeePayDetail.services_in_period}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="Services ($)"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.services_in_period || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Services in Last 20 Days"
                            currentValue={currentEmployeePayDetail.services_in_20d}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="Services 20D ($)"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.services_in_20d || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Service Rate"
                            currentValue={currentEmployeePayDetail.service_rate}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="Service Rate"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.service_rate || 0}
                            data={historicPayrolls}
                        />

                        <h3 className="mt-4 mb-1 text-xs font-bold uppercase tracking-wider text-gray-400 text-center">Sick & ESST</h3>

                        <YearBreakdownMetric
                            label="Sick Hours (in Period)"
                            yearData={currentEmployeePayDetail.sick_hours_in_period ?? currentEmployeePayDetail.sick_hours}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="Sick Hours"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => {
                                const emp = p.employee_data.find(e => e.user_id === userId);
                                return sumYearValues(emp?.sick_hours_in_period ?? emp?.sick_hours);
                            }}
                            data={historicPayrolls}
                        />

                        <YearBreakdownMetric
                            label="Year Sick Hours (YTD)"
                            yearData={currentEmployeePayDetail.year_sick_hours_through_period_end}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="YTD Sick Hours"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => sumYearValues(p.employee_data.find(e => e.user_id === userId)?.year_sick_hours_through_period_end)}
                            data={historicPayrolls}
                        />

                        <YearBreakdownMetric
                            label="Year Sick Hours (Before Period)"
                            yearData={currentEmployeePayDetail.YTD_sick_hours_prior_to_period ?? currentEmployeePayDetail.year_sick_hours_prior_to_period}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="Pre-Period Sick Hours"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => {
                                const emp = p.employee_data.find(e => e.user_id === userId);
                                return sumYearValues(emp?.YTD_sick_hours_prior_to_period ?? emp?.year_sick_hours_prior_to_period);
                            }}
                            data={historicPayrolls}
                        />

                        <YearBreakdownMetric
                            label="ESST Hours Paid"
                            yearData={currentEmployeePayDetail.esst_hours_paid_in_period}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="ESST Paid"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => sumYearValues(p.employee_data.find(e => e.user_id === userId)?.esst_hours_paid_in_period)}
                            data={historicPayrolls}
                        />

                        <YearBreakdownMetric
                            label="ESST Remaining"
                            yearData={currentEmployeePayDetail.esst_remaining}
                            isDollars={false}
                            graphLabelX="Period"
                            graphLabelY="ESST Remaining"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => sumYearValues(p.employee_data.find(e => e.user_id === userId)?.esst_remaining)}
                            data={historicPayrolls}
                        />

                        <h3 className="mt-4 mb-1 text-xs font-bold uppercase tracking-wider text-gray-400 text-center">Income</h3>
                        <p><strong>Pay Type:</strong> {currentEmployeePayDetail.income_in_period.pay_type}</p>

                        <TrendableMetric
                            label="Total Income per Hour Worked"
                            currentValue={(() => {
                                const totalIncome = toFloat(currentEmployeePayDetail.income_in_period?.total_income || 0);
                                const totalHours = toFloat(currentEmployeePayDetail.normal_hours || 0) + toFloat(currentEmployeePayDetail.overtime_hours || 0);
                                return totalHours > 0 ? totalIncome / totalHours : 0;
                            })()}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="Total Income / Hour"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => {
                                const emp = p.employee_data.find(e => e.user_id === userId);
                                if (!emp) return 0;

                                const income = toFloat(emp.income_in_period?.total_income || 0);
                                const hours = toFloat(emp.normal_hours || 0) + toFloat(emp.overtime_hours || 0);
                                return hours > 0 ? income / hours : 0;
                            }}
                            data={historicPayrolls}
                        />

                        {currentEmployeePayDetail.income_in_period.pay_type === "hourly" ? (
                            <>
                                <TrendableMetric
                                    label="Hourly Base + Merit"
                                    currentValue={currentEmployeePayDetail.income_in_period.hourly_rate}
                                    isDollars={true}
                                    graphLabelX="Period"
                                    graphLabelY="Hourly Base + Merit"
                                    openModalFn={openTrendModal}
                                    accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.hourly_rate || 0}
                                    data={historicPayrolls}
                                />
                                <TrendableMetric
                                    label="Normal Pay"
                                    currentValue={currentEmployeePayDetail.income_in_period.normal_hours_pay}
                                    isDollars={true}
                                    graphLabelX="Period"
                                    graphLabelY="Normal Pay"
                                    openModalFn={openTrendModal}
                                    accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.normal_hours_pay || 0}
                                    data={historicPayrolls}
                                />
                                <TrendableMetric
                                    label="Overtime Pay"
                                    currentValue={currentEmployeePayDetail.income_in_period.overtime_pay}
                                    isDollars={true}
                                    graphLabelX="Period"
                                    graphLabelY="Overtime Pay"
                                    openModalFn={openTrendModal}
                                    accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.overtime_pay || 0}
                                    data={historicPayrolls}
                                />
                                <TrendableMetric
                                    label="ESST Pay"
                                    currentValue={currentEmployeePayDetail.income_in_period.esst_pay}
                                    isDollars={true}
                                    graphLabelX="Period"
                                    graphLabelY="ESST Pay"
                                    openModalFn={openTrendModal}
                                    accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.esst_pay || 0}
                                    data={historicPayrolls}
                                />
                            </>
                        ) : (
                            <TrendableMetric
                                label="Commission"
                                currentValue={currentEmployeePayDetail.income_in_period.commission}
                                isDollars={true}
                                graphLabelX="Period"
                                graphLabelY="Commission"
                                openModalFn={openTrendModal}
                                accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.commission || 0}
                                data={historicPayrolls}
                            />
                        )}

                        <TrendableMetric
                            label="Tips"
                            currentValue={currentEmployeePayDetail.income_in_period.tips}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="Tips"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.tips || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="YTD Tips"
                            currentValue={computeYtdValue(
                                historicPayrolls,
                                userId,
                                "income_in_period.tips",
                                currentEmployeePayDetail.runMeta.period_end
                            )}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="YTD Tips"
                            openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentEmployeePayDetail.runMeta.period_end).getFullYear() })}
                            accessorFn={(p) =>
                                toFloat(p.employee_data.find(e => e.user_id === userId)?.income_in_period?.tips || 0)
                            }
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Bonus"
                            currentValue={currentEmployeePayDetail.income_in_period.bonus}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="Bonus"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.bonus || 0}
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="YTD Bonus"
                            currentValue={computeYtdValue(
                                historicPayrolls,
                                userId,
                                "income_in_period.bonus",
                                currentEmployeePayDetail.runMeta.period_end
                            )}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="YTD Bonus total"
                            openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentEmployeePayDetail.runMeta.period_end).getFullYear() })}
                            accessorFn={(p) =>
                                toFloat(p.employee_data.find(e => e.user_id === userId)?.income_in_period?.bonus || 0)
                            }
                            data={historicPayrolls}
                        />

                        <TrendableMetric
                            label="Total Income"
                            currentValue={currentEmployeePayDetail.income_in_period.total_income}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="Total Income"
                            openModalFn={openTrendModal}
                            accessorFn={(p) => p.employee_data.find(e => e.user_id === userId)?.income_in_period?.total_income || 0}
                            data={historicPayrolls}
                            rolesViewableTo={["owner"]}
                        />

                        <TrendableMetric
                            label="YTD Total Income"
                            currentValue={computeYtdValue(
                                historicPayrolls,
                                userId,
                                "income_in_period.total_income",
                                currentEmployeePayDetail.runMeta.period_end
                            )}
                            isDollars={true}
                            graphLabelX="Period"
                            graphLabelY="YTD Total Income"
                            openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentEmployeePayDetail.runMeta.period_end).getFullYear() })}
                            accessorFn={(p) =>
                                toFloat(p.employee_data.find(e => e.user_id === userId)?.income_in_period?.total_income || 0)
                            }
                            data={historicPayrolls}
                            rolesViewableTo={["owner"]}
                        />

                            </>
                        )}
                    </div>
                ) : (
                    <div className="p-4">Loading pay summary...</div>
                )}
            </GenericModal>

            {memoizedTrendGraphModal}
        </>
    );

};

export default React.memo(PaySummaryModal);
